// Route Feedback Data Model
export interface RouteFeedback {
  id: string;
  routeId: string;
  climberId: string;
  gymId: string;
  
  // Feedback components (all optional based on gym settings)
  stars?: number; // 1-5 stars
  suggestedGrade?: number; // Grade suggestion using same system as routes
  comment?: string;
  
  // Metadata
  createdAt: number; // timestamp
  updatedAt: number; // timestamp
}

// Gym settings for feedback system
export interface GymFeedbackSettings {
  enabled: boolean;
  allowStars: boolean;
  allowGradeSuggestions: boolean;
  allowComments: boolean;
  
  // Optional: Additional settings
  requireClimbToFeedback?: boolean; // Must have climbed route to give feedback
  moderateComments?: boolean; // Comments need approval
}

// Aggregated feedback data for displaying on routes
export interface RouteAggregatedFeedback {
  routeId: string;
  
  // Stars aggregation
  averageStars?: number;
  totalStarsCount: number;
  starsDistribution: { [key: number]: number }; // {1: count, 2: count, ...}
  
  // Grade suggestions aggregation
  suggestedGradeAverage?: number;
  suggestedGradeCount: number;
  suggestedGradeDistribution: { [key: number]: number };
  
  // Comments
  totalCommentsCount: number;
  
  // Metadata
  lastUpdated: number;
}

// Helper functions
export class RouteFeedbackHelper {
  static calculateAverageStars(feedbacks: RouteFeedback[]): number | null {
    const starsArray = feedbacks.filter(f => f.stars !== undefined).map(f => f.stars!);
    if (starsArray.length === 0) return null;
    return starsArray.reduce((sum, stars) => sum + stars, 0) / starsArray.length;
  }
  
  static calculateAverageSuggestedGrade(feedbacks: RouteFeedback[]): number | null {
    const gradesArray = feedbacks.filter(f => f.suggestedGrade !== undefined).map(f => f.suggestedGrade!);
    if (gradesArray.length === 0) return null;
    return gradesArray.reduce((sum, grade) => sum + grade, 0) / gradesArray.length;
  }
  
  static getStarsDistribution(feedbacks: RouteFeedback[]): { [key: number]: number } {
    const distribution: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    feedbacks.forEach(feedback => {
      if (feedback.stars && feedback.stars >= 1 && feedback.stars <= 5) {
        distribution[feedback.stars]++;
      }
    });
    return distribution;
  }
  
  static getGradeDistribution(feedbacks: RouteFeedback[]): { [key: number]: number } {
    const distribution: { [key: number]: number } = {};
    feedbacks.forEach(feedback => {
      if (feedback.suggestedGrade !== undefined) {
        const grade = feedback.suggestedGrade;
        distribution[grade] = (distribution[grade] || 0) + 1;
      }
    });
    return distribution;
  }
  
  static aggregateRouteFeedback(routeId: string, feedbacks: RouteFeedback[]): RouteAggregatedFeedback {
    const routeFeedbacks = feedbacks.filter(f => f.routeId === routeId);
    
    return {
      routeId,
      averageStars: this.calculateAverageStars(routeFeedbacks) || undefined,
      totalStarsCount: routeFeedbacks.filter(f => f.stars !== undefined).length,
      starsDistribution: this.getStarsDistribution(routeFeedbacks),
      suggestedGradeAverage: this.calculateAverageSuggestedGrade(routeFeedbacks) || undefined,
      suggestedGradeCount: routeFeedbacks.filter(f => f.suggestedGrade !== undefined).length,
      suggestedGradeDistribution: this.getGradeDistribution(routeFeedbacks),
      totalCommentsCount: routeFeedbacks.filter(f => f.comment && f.comment.trim().length > 0).length,
      lastUpdated: Date.now()
    };
  }
}
